/**
 * v1_956 / v1_957: the plain words for integration notices, shared by the
 * Integrations settings screen and the Outbound notifications section of the
 * Audit Record (both in src/MLRODashboard.jsx), so the two always describe a
 * notice the same way. Imports nothing and reads nothing.
 *
 * Every string a user or officer reads here has no hyphens or dashes
 * (tools/harness-integration-settings.mjs scans this file).
 */

// The test notice the settings screen queues (api/integrations-manage.js). It
// carries no client data; the worker sends it like any other notice.
export const TEST_EVENT_TYPE = 'integration.test';

// The updates a destination can take in stage one, in the order offered, with
// the sentence shown against each. Declined and monitoring alerts are guarded
// (plan section 3): only for a destination read by compliance staff only, and
// only after the warning is accepted.
export const INTEGRATION_UPDATES = [
  { type: 'client.approved', label: 'Client approved', help: 'When you approve a client.' },
  { type: 'client.risk_changed', label: 'Risk tier changed', help: 'When a client’s risk tier goes up or down.' },
  { type: 'client.review_due', label: 'Review due', help: 'When a client’s periodic review becomes due.' },
  { type: 'client.declined', label: 'Client declined', guarded: 'declined',
    help: 'Staff who use this system will see that a client was declined. If anyone there deals with the client directly, they must not discuss the reason. Only turn this on if that system is used by your compliance team.' },
  { type: 'client.monitoring_alert', label: 'Monitoring alert', guarded: 'monitoring',
    help: 'A monitoring alert can relate to matters you may not disclose to the client. Turn this on only for a system read by your compliance team.' },
];

// v1_959: the mark on a status API key. Only a key marked this way is told a
// client was declined; any other key reads "not approved", with no dates.
export const KEY_COMPLIANCE_ONLY_HELP = 'Tick this only if the system using this key is read by your compliance team. Only then does it see that a client was declined. Any other key sees "not approved", with no dates. If anyone there deals with the client directly, they must not discuss the reason.';

// One update in plain words, lower case for a sentence ("client approved").
export function updateWords(type) {
  if (type === TEST_EVENT_TYPE) return 'test notice';
  const u = INTEGRATION_UPDATES.find(x => x.type === type);
  return u ? u.label.toLowerCase() : 'notice';
}

export function fieldSetWords(fieldSet) {
  return fieldSet === 'full' ? 'Full details' : 'Status only';
}

// Why an attempt failed, in plain words (the worker's error categories).
const FAILURE_WORDS = {
  http_status: 'the destination did not accept it',
  address_refused: 'the address was refused',
  dns: 'the address could not be found',
  redirect: 'the destination tried to redirect it',
  timeout: 'no answer in time',
  secret_unavailable: 'the secret could not be read',
  too_large: 'too large to send',
  event_missing: 'the notice or its destination no longer exists',
  expired: 'more than 72 hours old',
  internal: 'an error on our side',
};

// A delivery's result, in plain words. d: a delivery row (state, attempts,
// last_status_code, last_error_category).
export function resultWords(d) {
  const row = d || {};
  const code = row.last_status_code ? ` (answered ${row.last_status_code})` : '';
  const why = FAILURE_WORDS[row.last_error_category] || '';
  switch (row.state) {
    case 'delivered': return `Delivered${code}`;
    case 'failed': return `Failed${why ? ', ' + why : ''}${code}`;
    case 'retry': return `Trying again${why ? ', ' + why : ''}${code}`;
    case 'sending': return 'Sending';
    case 'pending': return 'Waiting to send';
    case 'expired': return 'Expired, not sent';
    default: return 'Not known';
  }
}

// What was sent: the field names only, never values. A notice not sent yet
// has no list.
export function fieldsWords(fields, state) {
  if (Array.isArray(fields) && fields.length) return fields.join(', ');
  if (Array.isArray(fields) && state === 'delivered') return 'no client data (test notice)';
  return state === 'delivered' ? 'not recorded' : 'nothing sent yet';
}

// The Outbound notifications section of the Audit Record (v1_957).
// notices: the list from api/integrations-manage.js (action case_notices), or
// null when it could not be read. esc: the record's own HTML escape. fmt: its
// date format. Every value is escaped; the section is never silently empty.
export function outboundNotificationsHtml(notices, { esc, fmt }) {
  const when = (v) => (v ? esc(fmt(v)) : 'not recorded');
  let body;
  if (notices === null || notices === undefined || !Array.isArray(notices)) {
    body = '<p class="muted">The record of outbound notifications could not be read for this export, so this section may be incomplete. Export the record again, or ask CompliantID to check.</p>';
  } else if (!notices.length) {
    body = '<p class="muted">No outbound notifications were sent for this case.</p>';
  } else {
    const th = (t) => `<th style="text-align:left;font-size:11px;color:#6b7280;font-weight:600;padding:5px 8px;border-bottom:1px solid #e5e7eb">${t}</th>`;
    body = '<table><thead><tr>' + ['When', 'Update', 'Destination', 'What was sent', 'Result', 'Attempts'].map(th).join('') + '</tr></thead><tbody>'
      + notices.map(n => '<tr>'
        + `<td>${when(n.at)}</td>`
        + `<td>${esc(updateWords(n.type))}${n.resend ? ' <span class="muted">(sent again)</span>' : ''}</td>`
        + `<td>${esc(n.destination || 'destination removed')} <span class="muted">(${esc(fieldSetWords(n.field_set).toLowerCase())})</span></td>`
        + `<td>${esc(fieldsWords(n.fields_sent, n.state))}</td>`
        + `<td>${esc(resultWords(n))}</td>`
        + `<td>${esc(String(Number(n.attempts) || 0))}</td>`
        + '</tr>').join('')
      + '</tbody></table>'
      + '<p class="muted">Field names only. The values sent are not kept.</p>';
  }
  return `<h2>Outbound notifications</h2>\n  ${body}`;
}
